import React, { useState, useRef, useEffect } from 'react';
import './StatusFilter.scss';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography, List, ListItem, Checkbox, Button, Paper, Popper } from '@mui/material';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ArrowDropDown } from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { visuallyHidden } from '@mui/utils';


enum ServiceOptions {
  All,
  AirShopping,
  OfferPrice,
  SeatAvailability,
  OrderCreate,
  OrderRetrieve,
  OrderView,
  OrderReshop,
  OrderCancel,
  OrderChange
}

enum EnvironmentOptions {
  Production,
  Gold
}

const StatusFilter = ({ setFilter, filterBy = 'service' }) => {
  const [checked, setChecked] = useState([0]) as any;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const appliedRef = useRef<number[]>([0]);
  const open = Boolean(anchorEl);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [message, setMessage] = useState('')
  const { t } = useTranslation();
  const pendingFocusRef = useRef<'close' | 'trigger' | null>(null);

  useEffect(() => {
    if (!pendingFocusRef.current) return;
    const target = pendingFocusRef.current === 'close' ? closeRef.current : triggerRef.current;
    pendingFocusRef.current = null;
    target?.focus();
  }, [checked]);

  const handleClose = () => {
    setAnchorEl(null);
    setChecked(appliedRef.current);
    triggerRef.current?.focus(); // return focus to trigger, standard disclosure pattern
    pendingFocusRef.current = 'trigger';
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // Ignore clicks on the trigger; its own onClick handles toggling.
    if (triggerRef.current?.contains(event.target as Node)) return;
    setAnchorEl(null);
    setChecked(appliedRef.current);
  };

  const toggle = (index: number) => {
    if (index === 0) { if (!checked.includes(0)) setChecked([0]); return; }
    checked.includes(index)
      ? setChecked(checked.filter((i) => i !== index).sort((a, b) => a - b))
      : setChecked([...checked, index].filter((i) => i !== 0).sort((a, b) => a - b));
  };

  const handleApply = () => {
    const names = checked
      .map((i) => (filterBy === 'service' && ServiceOptions[i] !== 'All' ? ServiceOptions[i] : null))
      .filter(Boolean);
    appliedRef.current = [...checked]
    setFilter(names);
    handleClose();
  };

  const handleReset = () =>{
    setChecked([0])
    pendingFocusRef.current = 'close';
    requestAnimationFrame(() => setMessage(`${t('release_notes.resetAnnouncemnt')}`));
    closeRef.current?.focus()
  } 
  useEffect(()=>{
    if(checked.length === 0){
      setChecked([0]);
    }
  },[checked])
  useEffect(() => {
      if (!message) return;
      const timeout = setTimeout(() => setMessage(''), 1000);
      return () => clearTimeout(timeout);
    }, [message]);

  const isAllSelected = checked.length === 1 && ServiceOptions[checked[0]] === 'All';
  // Apply should only be disabled when there is nothing pending to apply,
  // i.e. the current selection is "All" AND it matches what's already applied.
  // user was trying to apply a reset from a previously-applied filter set.
  const isSameAsApplied =
    checked.length === appliedRef.current.length &&
    checked.every((value: number) => appliedRef.current.includes(value));
  const isApplyDisabled = isAllSelected && isSameAsApplied;

  return (
    <div>
      <Button
        ref={triggerRef}
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${ServiceOptions[checked[0]]==='All'?`${t('release_notes.buttonAriaLabel')}`:`${t('release_notes.buttonAriaLabelSeleceted',{count:checked.length})}`}`}
        variant='primary'
        size='xs'
        sx={{
          width: 'auto !important',
          backgroundColor:!open?'#1460AA':'#002345'
        }}
      >
        <FilterListIcon sx={{ color: 'white', width: '16px' }} />
        <Typography component="span" sx={{ fontSize: '14px', fontWeight: 700 }}>{ServiceOptions[checked[0]]==='All'?t('release_notes.filterLabelPlural'):`${t('release_notes.filterLabelPlural')} (${checked.length})`}</Typography>
        <ArrowDropDown sx={{ color: 'white', rotate: open ? '180deg' : '0deg' }}></ArrowDropDown>

      </Button>

      <Popper
        role="dialog"
        aria-modal="true"
        open={open}
        anchorEl={anchorEl}
        sx={{ zIndex: 1 }}
        placement="bottom"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ]}
      >
        <TrapFocus open={open}>
          {/* tabIndex={-1} = not itself a Tab stop; TrapFocus needs a container node */}
          <Paper
            tabIndex={-1}
            sx={{minWidth:'229px', boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)', borderRadius: '16px', padding: '16px', '& :focus-visible': { outline: '2px solid black', outlineOffset: '-2px' } }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleClose();
              // No Tab handling here — native order does the work.
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width:'100%' }}>
              <Typography sx={{ fontWeight: 700 }}>{t('release_notes.apiAffected')}</Typography>
              <Button ref={closeRef} aria-label={`${t('release_notes.closeLabel')}`} variant='text' size='md'onClick={handleClose} sx={{ fontSize: '32px', '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' }, justifyContent:'end !important' }} ><CloseIcon aria-hidden='true'></CloseIcon></Button>
            </Box>

            <List sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'flex-start',
              listStyle: 'none',
              mb:'8px',
              mt:'8px'
            }}>
              {Object.values(filterBy === 'service' ? ServiceOptions : EnvironmentOptions)
                .filter((o) => typeof o === 'string')
                .map((option, index) => (
                  <ListItem key={option as string} disablePadding>
                    
                    <Checkbox
                      id={`checkbox-${index}`}
                      checked={checked.includes(index)}
                      sx={{ p: '0', mr: '10px', color:'#1460AA', '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px', justifyContent:'end !important', '&:focus-visible':{outline:'2px solid black'} } }}
                      onChange={() => toggle(index)}
                      inputProps={{'aria-labelledby':`label-${index}`}}
                    // no tabIndex override — real <input>, natural DOM-order tab stop
                    />
                    <label aria-hidden={true} id={`label-${index}`} aria-labelledby={`option-position-${index} label-${index}`} htmlFor={`checkbox-${index}`}><Box id={`option-position-${index}`} sx={visuallyHidden}>{`${index+1} of ${Object.keys(ServiceOptions).filter(k=>isNaN(Number(k))).length}`}</Box> {option as string}</label>
                  </ListItem>
                ))}
            </List>


            <Box sx={{ display: 'flex', flexDirection: 'column', gap:'16px'}}>
              <Button aria-label={`${t('release_notes.applyButtonAriaLabel',{ count: checked.length })}`} disabled={isApplyDisabled} variant='primary' size='xs' sx={{width:'100%',
                '&.Mui-disabled': {
                  backgroundColor: '#D6D7D9',
                  color: '#6D6D6F',
                },
              }} onClick={handleApply}>{t('release_notes.applyButton')}</Button>
              <Button disabled={checked.length === 1 && ServiceOptions[checked[0]] === 'All'} variant='secondary' size='xs' sx={{width:'100%',
                '&.Mui-disabled': {
                  backgroundColor: '#EFF0F2',
                  color: '#A3A4A6',
                  border: '1px solid #C9CACC',
                },
                }}
               onClick={handleReset}>{t('release_notes.resetButton')}</Button>
               <Box aria-live="polite" sx={visuallyHidden}>{message}</Box>
            </Box>
          </Paper>
        </TrapFocus>
      </Popper>
    </div>
  );
};
export default StatusFilter;